/**
 * Node.js environment implementation.
 * Uses fs and crypto built-in modules.
 */
import type { NodeFileInfo, MediaDimensions, HashAlgorithm } from './types'
import {
  formatSize,
  formatTime,
  getExtension,
  getStem,
  getBaseName,
  inferMimeType,
  DEFAULT_CHUNK_SIZE,
} from './core'

/**
 * Read image dimensions from a file header.
 * Supports PNG, JPEG, GIF, WebP, BMP — zero external dependencies.
 */
async function getImgDimensionsNode(filePath: string): Promise<MediaDimensions | null> {
  const fs = await import('fs')
  try {
    const fd = await fs.promises.open(filePath, 'r')
    try {
      const header = Buffer.alloc(32)
      await fd.read(header, 0, 32, 0)

      // PNG: first 8 bytes are signature, then IHDR at offset 16
      if (header[0] === 0x89 && header[1] === 0x50 && header[2] === 0x4e && header[3] === 0x47) {
        const width = header.readUInt32BE(16)
        const height = header.readUInt32BE(20)
        return { width, height }
      }

      // JPEG: scan for SOF0/SOF2 marker
      if (header[0] === 0xff && header[1] === 0xd8) {
        // Need to read more of the file to find SOF marker
        const buf = Buffer.alloc(64 * 1024)
        const { bytesRead } = await fd.read(buf, 0, buf.length, 0)
        let i = 2
        while (i < bytesRead - 8) {
          if (buf[i] !== 0xff) { i++; continue }
          const marker = buf[i + 1]
          // SOF0 (0xc0) or SOF2 (0xc2) = Start of Frame
          if (marker === 0xc0 || marker === 0xc2) {
            const height = buf.readUInt16BE(i + 5)
            const width = buf.readUInt16BE(i + 7)
            return { width, height }
          }
          // Skip to next marker
          const segmentLength = buf.readUInt16BE(i + 2)
          i += 2 + segmentLength
        }
        return null
      }

      // GIF: bytes 6-10 = logical screen descriptor
      if (header[0] === 0x47 && header[1] === 0x49 && header[2] === 0x46) {
        const width = header.readUInt16LE(6)
        const height = header.readUInt16LE(8)
        return { width, height }
      }

      // WebP: RIFF container
      if (header[0] === 0x52 && header[1] === 0x49 && header[2] === 0x46 && header[3] === 0x46) {
        // Check sub-format (VP8 / VP8L / VP8X)
        const subFormat = header.toString('ascii', 8, 12)
        if (subFormat === 'VP8 ') {
          // Read more for VP8 dimensions
          const vp8Buf = Buffer.alloc(10)
          await fd.read(vp8Buf, 0, 10, 30)
          // VP8 frame header: width and height are 14-bit values stored in 3 bytes
          const w = vp8Buf.readUInt16LE(6) & 0x3fff
          const h = vp8Buf.readUInt16LE(8) & 0x3fff
          return { width: w, height: h }
        }
        if (subFormat === 'VP8L') {
          const vp8lBuf = Buffer.alloc(5)
          await fd.read(vp8lBuf, 0, 5, 21)
          const bits = vp8lBuf.readUInt32LE(1)
          const width = (bits & 0x3fff) + 1
          const height = ((bits >> 14) & 0x3fff) + 1
          return { width, height }
        }
        if (subFormat === 'VP8X') {
          const width = header.readUInt32LE(24) & 0xffffff + 1
          const height = (header.readUInt32LE(26) >> 8) + 1
          return { width, height }
        }
      }

      // BMP
      if (header[0] === 0x42 && header[1] === 0x4d) {
        const width = Math.abs(header.readInt32LE(18))
        const height = Math.abs(header.readInt32LE(22))
        return { width, height }
      }

      return null
    } finally {
      await fd.close()
    }
  } catch {
    return null
  }
}

/**
 * Compute file hash using Node.js crypto with streaming.
 */
async function computeHashNode(filePath: string, algorithm: HashAlgorithm): Promise<string> {
  const fs = await import('fs')
  // Dynamic import crypto to avoid bundling issues
  const hashAlgo = algorithm === 'md5' ? 'md5' : 'sha256'
  // Use require to get crypto (it's a Node.js built-in)
  const crypto = await import('crypto')
  return new Promise((resolve, reject) => {
    const hash = crypto.createHash(hashAlgo)
    const stream = fs.createReadStream(filePath, { highWaterMark: DEFAULT_CHUNK_SIZE })
    stream.on('data', (chunk: Buffer) => hash.update(chunk))
    stream.on('end', () => resolve(hash.digest('hex')))
    stream.on('error', reject)
  })
}

/**
 * Node.js implementation: extract file info from a file path.
 */
export async function getFileInfoFromNode(filePath: string): Promise<NodeFileInfo> {
  const fs = await import('fs')
  const path = await import('path')

  const absolutePath = path.resolve(filePath)
  const stat = await fs.promises.stat(absolutePath)

  const name = getBaseName(absolutePath)
  const mimeType = inferMimeType(name)
  const extension = getExtension(name)

  // Run hash computation and image detection in parallel
  const [md5, sha256, dimensions] = await Promise.all([
    computeHashNode(absolutePath, 'md5').catch(() => ''),
    computeHashNode(absolutePath, 'sha256').catch(() => ''),
    stat.isFile() ? getImgDimensionsNode(absolutePath) : Promise.resolve(null),
  ])

  return {
    name,
    stem: getStem(name),
    extension,
    size: stat.size,
    sizeFormatted: formatSize(stat.size),
    mimeType,
    md5,
    sha256,
    lastModified: stat.mtimeMs,
    lastModifiedFormatted: formatTime(stat.mtimeMs),
    absolutePath,
    birthtime: stat.birthtimeMs,
    birthtimeFormatted: formatTime(stat.birthtimeMs),
    atime: stat.atimeMs,
    atimeFormatted: formatTime(stat.atimeMs),
    mode: stat.mode,
    isDirectory: stat.isDirectory(),
    isSymlink: stat.isSymbolicLink(),
    ino: stat.ino,
    ...(dimensions || {}),
  }
}
